import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import BrandLogo from './BrandLogo';
// The POS top bar IS the guest booking screen's header — same lp-header /
// lp-brand / lp-nav / lp-book-btn design App.jsx already reuses for the
// booking flow (see App.jsx's header markup). Reused class-for-class rather
// than re-implemented so the two screens cannot drift, and so a change to
// Config -> Theme (which feeds --jt-header-bg, --jt-brand, --jt-cyan-deep)
// moves both at once.
import '../css/LandingPage.css';
import '../css/Pos.css';

/**
 * Point of Sale top bar: brand, screen tabs, and the signed-in staff account.
 *
 * The booking header's nav links double as the POS's screen tabs here, which
 * is why the old separate tab strip is gone: below 860px LandingPage.css
 * collapses .lp-nav into the .lp-nav-mobile dropdown, so the tabs stop being
 * a horizontally-scrolling row on the counter tablet and become a normal
 * menu.
 *
 * @param {object} props
 * @param {string} props.courtName - club name shown beside the logo
 * @param {string} [props.logoUrl] - config.logoUrl; falsy renders the placeholder mark
 * @param {Array<{key: string, label: string}>} props.tabs - screens to offer, e.g. POS_TABS
 * @param {string} props.activeTab - key of the screen currently shown
 * @param {(key: string) => void} props.onTabChange - called with the key of the tab tapped
 * @param {string} props.signedInEmail - staff account currently on the till
 * @param {() => void} props.onSignOut - called when Sign out is tapped
 * @returns {JSX.Element}
 */
function PosHeader({
  courtName,
  logoUrl,
  tabs,
  activeTab,
  onTabChange,
  signedInEmail,
  onSignOut,
}) {
  const [navOpen, setNavOpen] = useState(false);

  // Same dismissal the booking header uses (App.jsx): a tap anywhere outside
  // the header closes the menu, so it can never be left hanging open over the
  // till between customers.
  useEffect(() => {
    if (!navOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (!event.target.closest('.lp-header')) setNavOpen(false);
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [navOpen]);

  const handleTabClick = (key) => {
    onTabChange(key);
    setNavOpen(false);
  };

  /**
   * Renders the screen tabs as booking-header nav links.
   * Called twice — once for the desktop nav, once for the mobile dropdown —
   * so the two lists cannot fall out of step.
   *
   * @returns {JSX.Element[]}
   */
  const renderTabs = () => tabs.map(({ key, label }) => (
    <button
      key={key}
      type="button"
      role="tab"
      aria-selected={activeTab === key}
      className={`lp-nav-link pos-nav-link${activeTab === key ? ' active' : ''}`}
      onClick={() => handleTabClick(key)}
    >
      {label}
    </button>
  ));

  return (
    <header className="lp-header">
      <div className="lp-header-inner pos-header-inner">
        {/* Not clickable, unlike the booking header's brand: there is no
            landing page to go back to from behind the counter. */}
        <div className="lp-brand">
          <BrandLogo src={logoUrl} alt={courtName} className="lp-logo" />
          <span className="lp-brand-name">{courtName}</span>
          <span className="pos-badge">POS</span>
        </div>

        <nav className="lp-nav" role="tablist" aria-label="POS screens">
          {renderTabs()}
        </nav>

        {/* Sits where the booking header's "Book a Court" button sits, and
            borrows its pill: Sign out is the one action this header owns. */}
        <div className="pos-header-account">
          <span className="pos-user" title={signedInEmail}>{signedInEmail}</span>
          <button type="button" className="lp-book-btn" onClick={onSignOut}>Sign out</button>
        </div>

        <button
          type="button"
          className="lp-nav-toggle"
          onClick={() => setNavOpen(open => !open)}
          aria-label="Toggle menu"
          aria-expanded={navOpen}
        >
          {navOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {navOpen && (
        <nav className="lp-nav-mobile" role="tablist" aria-label="POS screens">
          {renderTabs()}
          <span className="pos-user pos-user-mobile" title={signedInEmail}>{signedInEmail}</span>
          <button type="button" className="lp-book-btn" onClick={onSignOut}>Sign out</button>
        </nav>
      )}
    </header>
  );
}

export default PosHeader;
